import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Timeline from '@mui/lab/Timeline';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import CascadingSidePanel from './CascadingSidePanel';
import { formatTo12Hour } from '../../utils/formatters';

interface Stage {
  id?: string;
  stage: string;
  status: string;
  timestamp?: string;
  completed_at?: string;
  completedAt?: string;
  created_at?: string;
  createdAt?: string;
  completedBy?: string;
  workerName?: string;
  workerId?: string;
  user_name?: string;
  assignedToName?: string;
  assigned_to?: string;
  assignedTo?: string;
  worker?: string;
  user?: string;
  completedByUser?: string;
}

interface Item {
  id: string;
  type: string;
  width?: string;
  height?: string;
  number?: string;
  status?: string;
  stages?: Stage[];
  logs?: Array<{
    stage: string;
    status: string;
    user_name?: string;
    user_id?: string;
    completed_at?: string;
  }>;
}

interface StageSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  item: Item | null;
  onBackToItems: () => void;
  title?: string;
}

const StageSidePanel: React.FC<StageSidePanelProps> = ({
  isOpen,
  onClose,
  onCloseAll,
  item,
  onBackToItems,
  title,
}) => {
  const capitalizeWords = (str: string) => {
    return str.replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to check if an item is a panel with laser cutting enabled
  const hasLaserCutting = (item: Item | null): boolean => {
    if (!item) return false;
    const isPanel = item.type === 'Panel';
    if (!isPanel) return false;
    
    const isLaserCutting = (item as any).is_laser_cutting ?? (item as any).isLaserCutting;
    return isLaserCutting === true || isLaserCutting === 'true' || isLaserCutting === 1;
  };

  // Debug logging
  console.log('StageSidePanel - Item data:', item);
  console.log('StageSidePanel - Item stages:', item?.stages);
  console.log('StageSidePanel - Stage worker data:', item?.stages?.map(s => ({
    stage: s.stage,
    status: s.status,
    completedBy: s.completedBy,
    workerName: s.workerName,
    workerId: s.workerId,
    assigned_to: (s as any).assigned_to,
    worker: (s as any).worker,
    allFields: Object.keys(s)
  })));
  
  // Log each stage individually for better debugging
  item?.stages?.forEach((stage, index) => {
    console.log(`StageSidePanel - Stage ${index}:`, {
      stage: stage.stage,
      status: stage.status,
      allFields: Object.keys(stage),
      workerFields: {
        completedBy: stage.completedBy,
        workerName: stage.workerName,
        workerId: stage.workerId,
        assigned_to: (stage as any).assigned_to,
        worker: (stage as any).worker,
        assignedTo: (stage as any).assignedTo,
        completedByUser: (stage as any).completedByUser,
        user: (stage as any).user
      }
    });
  });

  const stages = item?.stages || [];
  const totalStages = stages.length;
  const completedStages = stages.filter(stage => stage.status === 'completed').length;

  console.log('StageSidePanel - Total stages:', totalStages);
  console.log('StageSidePanel - Completed stages:', completedStages);

  useEffect(()=>{
    console.log("stages:",stages)
  },[stages])

  return (
    <CascadingSidePanel
      isOpen={isOpen}
      onClose={onClose}
      onCloseAll={onCloseAll || onClose}
      panelId="stage-panel"
      title={title || `${capitalizeWords(item?.type || '')} - Stages`}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
          {/* Side Panel Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={onBackToItems}
                size="small"
                sx={{ mr: 1 }}
              >
                ←
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {title || `${capitalizeWords(item.type || '')} - Stages`}
              </Typography>
            </Box>
            <IconButton 
              onClick={onClose}
              size="small"
            >
              ✕
            </IconButton>
          </Box>

      {/* Side Panel Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent:'flex-start', alignContent:'flex-start' }}>
          {/* Laser Cutting Indicator - Display at top for panels with laser cutting */}
          {hasLaserCutting(item) && (
           
                <Chip
                  label="Laser Cutting"
                  size="small"
                  sx={{
                    fontSize: '0.8rem',
                    height: 24,
                    fontWeight: 700,
                    color: '#d32f2f', // Red text
                    backgroundColor: '#ffcdd2', // Slightly darker red background
                    border: '1px solid #ef9a9a'
                  }}
                />
             
          )}

          {/* Item Info Header */}
          <Card sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Left side */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {item.number || item.id}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {`${parseInt(item.width)}w × ${item.height ? parseInt(item.height) + "h" : '-'}`}
                </Typography>
              </Box>

              {/* Right side */}
              <Box sx={{ display: 'flex', flexDirection:'column' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, textAlign:'center' }}>
                  {completedStages}/{totalStages}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                stages completed
              </Typography> */}
              </Box>
            </Box>         
          </Card>

          {/* Stages Progress */}
          {/* <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Stages Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {completedStages}/{totalStages}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                stages completed
              </Typography>
            </Box>
            <LinearProgress                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                     
              variant="determinate" 
              value={totalStages > 0 ? (completedStages / totalStages) * 100 : 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
            
            {(() => {
              const completedStagesWithWorkers = stages
                .filter(s => s.status === 'completed' && (s.workerName || s.completedBy || (s as any).assigned_to || (s as any).worker || (s as any).assignedTo || (s as any).completedByUser || (s as any).user))
                .map(s => s.workerName || s.completedBy || (s as any).assigned_to || (s as any).worker || (s as any).assignedTo || (s as any).completedByUser || (s as any).user)
                .filter((worker, index, arr) => arr.indexOf(worker) === index); // Remove duplicates
              
              if (completedStagesWithWorkers.length > 0) {
                return (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      Workers involved:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {completedStagesWithWorkers.map((worker, index) => (
                        <Chip
                          key={index}
                          label={worker}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              }
              return null;
            })()}
          </Card> */}

          {/* Stages List */}
          {stages.length > 0 ? (
            <Timeline sx={{
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
      }}>  
              {stages.map((stage, index) => {
                const isCompleted = stage.status === "completed";
                const isPending = stage.status === "pending";
                const isInProgress = stage.status === "in_progress";

                return (
                  <TimelineItem key={stage.id || `${stage.stage}-${index}`}>
                    <TimelineSeparator >
                      {/* <TimelineDot
                        sx={{
                          padding:0,
                          // bgcolor: isCompleted
                          //   ? "success.main"
                          //   : isInProgress
                          //   ? "primary.main"
                          //   : "grey.400",
                        }}
                      >
                        {isCompleted ? (
                          <CheckIcon  sx={{ color: "#fff" }} fontSize="small" />
                        ) : (
                          <AccessTimeIcon sx={{ color: "#fff" }} fontSize="small" />
                        )}
                      </TimelineDot> */}
                      {
                        isCompleted ? (
                          <TimelineDot variant="filled" color='success' />
                        ):(
                          <TimelineDot variant="outlined" />
                        )
                      }
                      
                      {index !== stages.length - 1 && (
                        <TimelineConnector
                          sx={{
                            bgcolor: isCompleted ? "success.main" : "grey.400",
                          }}
                        />
                      )}
                    </TimelineSeparator>

                    <TimelineContent sx={{ pb: 2}}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {stage.stage === "Dispatch"
                            ? "Ready To Dispatch"
                            : stage.stage}
                        </Typography>
                        {/* <Chip
                          label={
                            stage.status === "completed"
                              ? "Completed"
                              : stage.status === "in_progress"
                              ? "In Progress"
                              : "Pending"
                          }
                          color={
                            isCompleted
                              ? "success"
                              : isInProgress
                              ? "primary"
                              : "default"
                          }
                          size="small"
                        /> */}
                      </Box>

                      {/* Completed info */}
                      {isCompleted && (() => {
                        // Try all possible field names for worker name from stage object
                        let workerName = 
                        (stage as any).userName || 

                          stage.user_name || 
                          stage.assignedToName || 
                          stage.workerName || 
                          stage.completedBy || 
                          stage.assigned_to || 
                          stage.worker ||
                          stage.assignedTo ||
                          stage.completedByUser ||
                          stage.user ||

                          null;
                        
                        // If not found in stage, try to find it in item logs (fallback)
                        if (!workerName && item.logs && Array.isArray(item.logs)) {
                          const completionLog = item.logs.find((log: any) => 
                            log.stage === stage.stage && 
                            (log.status === 'completed' || log.status === 'COMPLETED')
                          );
                          workerName = completionLog?.user_name || null;
                        }
                        
                        // Debug logging for completed stages without worker name
                        if (!workerName) {
                          console.warn('StageSidePanel - No worker name found for completed stage:', {
                            stage: stage.stage,
                            status: stage.status,
                            allFields: Object.keys(stage),
                            stageObject: stage,
                            itemLogs: item.logs,
                            itemLogsCount: item.logs?.length || 0
                          });
                        }
                        
                        return workerName ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            <Typography variant="subtitle2" fontWeight={600} >
                              {workerName}
                            </Typography>
                          </Typography>
                        ) : null;
                      })()}

                      {/* Timestamp */}
                      {isCompleted && (() => {
                        // Prefer completed_at (actual completion time) over timestamp
                        const completionTime = (stage as any).completed_at || (stage as any).completedAt || stage.timestamp;
                        
                        if (!completionTime) {
                          return null;
                        }
                        
                        try {
                          const completionDate = new Date(completionTime);
                          if (isNaN(completionDate.getTime())) {
                            return null;
                          }
                          
                          // Get local time components (not UTC)
                          const hours = completionDate.getHours();
                          const minutes = completionDate.getMinutes();
                          const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                          
                          return (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              {completionDate.toLocaleDateString()}{" "}
                              {formatTo12Hour(stage?.created_at || stage?.createdAt || completionTime)}
                            </Typography>
                          );
                        } catch (error) {
                          console.error('Error formatting completion time:', error);
                          return null;
                        }
                      })()}
                      
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '150px',
              textAlign: 'center'
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No stages found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This part doesn't have any stages yet.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      </Box>
    </CascadingSidePanel>
  );
};

export default StageSidePanel;
